import * as React from 'react';
import {useState, useEffect} from 'react';
import {Modal, Row, Col, Table, Button} from 'antd';
import {AiFillEnvironment} from 'react-icons/ai';
import {useIntl} from 'react-intl';
import {DistrictsListDto} from 'src/shared/services/configuration/countries/dtos/districts-list-dto';
import MainUtils from 'src/shared/utils/main';
import {countriesService} from 'src/shared/services/configuration/countries';
import {
  INDEX_PAGE_SIZE_DEFAULT,
  PAGE_NUMBER,
} from 'src/shared/constants/AppConst';
import TableColumn from './columns';
import {FaPlus} from 'react-icons/fa';
import CreateOrUpdateDistrictModal from './create-or-update-district';
import {popupConfirm} from 'src/shared/components/popup-confirm';
import {useLocaleContext} from 'src/domain/utility/AppContextProvider/LocaleContextProvide';
import useTableV2 from 'src/shared/hooks/useTableV2';

interface DistrictsModalProps {
  visible: boolean;
  regionId: string;
  onFinish: () => void;
  onCancel: () => void;
}

const DistrictsModal: React.FC<DistrictsModalProps> = (props) => {
  const {visible, regionId, onFinish, onCancel} = props;
  const {messages} = useIntl();
  const {showTotal, ...tableV2} = useTableV2();
  const [districtsList, setDistrictsList] = useState<DistrictsListDto[]>([]);
  const [districtsListTotal, setDistrictsListTotal] = useState(0);
  const [isDistrictsListLoading, setIsDistrictsListLoading] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [districtDetails, setDistrictDetails] = useState<DistrictsListDto>();

  const [pagination, setPagination] = useState({
    page: PAGE_NUMBER,
    pageSize: INDEX_PAGE_SIZE_DEFAULT,
  });

  const [addDistricttModalVisbility, setAddDistricttModalVisbility] =
    useState(false);

  const {locale}: any = useLocaleContext();

  useEffect(() => {
    if (!MainUtils.isEmptyValue(regionId)) {
      (async () => {
        try {
          setIsDistrictsListLoading(true);
          const districtsAll = await countriesService.districts({
            regionId: regionId ?? '',
            Page: pagination.page,
            PageSize: pagination.pageSize,
          });
          setDistrictsList(districtsAll.Data);
          setDistrictsListTotal(districtsAll.Total);
        } catch (error) {
          console.error(error);
        } finally {
          setIsDistrictsListLoading(false);
        }
      })();
    } else setDistrictsList([]);
  }, [regionId, pagination, refresh]);

  const openCreateOrUpdateDistrictModal = () => {
    return (
      <CreateOrUpdateDistrictModal
        visible={addDistricttModalVisbility}
        regionId={regionId}
        districtDetails={districtDetails}
        onCancel={() => {
          setAddDistricttModalVisbility(false);
        }}
        onFinish={() => {
          setAddDistricttModalVisbility(false);
          setRefresh((prev: boolean) => !prev);
        }}
      />
    );
  };

  const deleteDistrict = (district: DistrictsListDto) => {
    popupConfirm(async () => {
      try {
        await countriesService.districtDelete(district.Id);
        setRefresh((prev: boolean) => !prev);
      } catch (error) {
        console.error(error);
      }
    }, `${messages['commom.areYouSureYouWantToDelete'].toString()}` + ' ' + [district?.Locales?.find((item) => item.LanguageId === locale.code)?.LocaleValue ?? district?.Name] + `?`);
  };

  const updateDistrict = (district: DistrictsListDto) => {
    setDistrictDetails(district);
    setAddDistricttModalVisbility(true);
  };

  const tablePagination = {
    total: districtsListTotal,
    pageSize: pagination.pageSize,
    current: pagination.page,
    showTotal,
    onShowSizeChange: async (current: any, PageSize: any) => {
      setPagination({pageSize: PageSize, page: current});
    },
    onChange: (page: any, pageSize: any) => {
      setPagination({pageSize: pageSize, page: page});
    },
  };

  return (
    <Modal
      width={'800px'}
      onCancel={() => onCancel()}
      centered
      open={visible}
      title={
        <>
          <div className='text-title'>
            <span className='icon-title'>
              <AiFillEnvironment />
            </span>
            {messages['countries.region.districts'].toString()}
          </div>
        </>
      }
      footer={false}>
      <Row gutter={[0, 30]}>
        <Col span={24}>
          <Button
            icon={<FaPlus className='add-button-icon' />}
            onClick={() => {
              setAddDistricttModalVisbility(true);
              setDistrictDetails(undefined);
            }}
            type='primary'
            className='add-button'>
            {messages['countries.region.districts.addNewDistrict'].toString()}
          </Button>
        </Col>
        <Col span={24}>
          <Table
            columns={TableColumn(deleteDistrict, updateDistrict)}
            pagination={tablePagination}
            loading={isDistrictsListLoading}
            dataSource={districtsList}
            rowKey={(record: DistrictsListDto) => record.Id?.toString()}
            bordered={true}
            {...tableV2}
          />
        </Col>
      </Row>
      {openCreateOrUpdateDistrictModal()}
    </Modal>
  );
};

export default DistrictsModal;
